import React, { useState, useContext, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as yup from "yup";
import moduleConfig from "./moduleConfig";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import { ContainerLoading } from "../../UI/loading/ContainerLoading";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import {
  getFoodTimeByMealId,
  getMealIdByFoodTime,
  isDatePassed,
  isToday,
  isTomorrow,
} from "../../../helpers/CommonHelper";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";

import {
  updateInitialState,
  updateReducer,
} from "../../../reducers/module/updateReducer";
import {
  loadItemGeneralPost,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormDate from "../../UI/forms/CMSFormDate";
import CMSFormMultiSelect from "../../UI/forms/CMSFormMultiSelect";

const MenuUpdate = () => {
  const [updateState, updateDispatch] = useReducer(
    updateReducer,
    updateInitialState
  );
  const [formInitialValues, setFormInitialValues] = useState({});
  const [foodOptions, setFoodOptions] = useState([]);
  const { setBreadcrumb } = useContext(BreadcrumbContext);

  const { meal, id } = useParams();
  const navigate = useNavigate();

  // Fetch menu data
  const loadItem = async () => {
    await loadItemGeneralPost(
      id,
      `${moduleConfig.url}/get-data-for-set-menu-form`,
      updateDispatch,
      { date: id }
    );
  };

  useEffect(() => {
    loadItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      moduleConfig.breadcrumbs.update,
    ]);
  }, [id]);

  useEffect(() => {
    if (updateState.data.item && updateState.data.item._id) {
      setFormInitialValues(updateState.data.item);
    }
  }, [updateState.data]);

  return (
    <DashboardLayout>
      {updateState.status === "fetching" && updateState.isLoading && (
        <ContainerLoading />
      )}
      {updateState.error && updateState.status === "fetchFailed" ? (
        <BlockErrorMessage error={updateState.error} />
      ) : (
        <>
          <ModuleHeader
            moduleTitle={`Update ${moduleConfig.singleItemTitle}`}
            moduleUrl={moduleConfig.url}
          />

          {updateState.data && (
            <section className="content">
              <Card>
                <Card.Body>
                  {updateState.isLoading && (
                    <div className="overlay position-absolute containerLoading">
                      <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                    </div>
                  )}

                  <Formik
                    initialValues={{
                      ...formInitialValues,
                      date: id,
                      mealId: getFoodTimeByMealId(
                        updateState.data?.meals,
                        meal
                      ),
                      foodIds:
                        updateState.data?.selectedItems?.[meal]?.map(
                          (item) => item._id
                        ) || [],
                    }}
                    validationSchema={yup.object({
                      date: yup.string().required("Date is required."),
                      mealId: yup.string().required("Food time is required."),
                      foodIds: yup
                        .array()
                        .min(1, "Select at least one food item.")
                        .required("Food items are required."),
                    })}
                    enableReinitialize={true}
                    onSubmit={async (values) => {
                      let mealId = getMealIdByFoodTime(
                        updateState.data?.meals,
                        values.mealId
                      );
                      values.mealId = mealId;

                      await onSubmitGeneral(
                        values,
                        `${moduleConfig.url}/set-menu`,
                        `back`,
                        updateDispatch,
                        navigate
                      );
                    }}
                  >
                    {(form) => (
                      <Form onSubmit={form.handleSubmit}>
                        {updateState.error && (
                          <FormErrorMessage error={updateState.error} />
                        )}

                        {/* Date Field */}
                        <CMSFormDate
                          name="date"
                          label="Date"
                          required={true}
                          value={
                            form.values["date"]
                              ? new Date(form.values["date"])
                              : null
                          }
                          form={form}
                          disabled={true}
                        />

                        {/* Food Time Dropdown */}
                        <CMSFormInput
                          name="mealId"
                          label="Food Time"
                          required={true}
                          form={form}
                          disabled
                        />
                        <CMSFormMultiSelect
                          name="foodIds"
                          label="Food Items"
                          customName={true}
                          selectionValue="_id"
                          selected={form.values.foodIds}
                          required={true}
                          form={form}
                          options={
                            updateState.data?.foods
                              ? updateState.data.foods.filter(
                                  (food) => food.foodTime === form.values.mealId
                                )
                              : []
                          }
                          displayValue="title"
                        />
                        {!isDatePassed(id) &&
                          !isToday(id) &&
                          !isTomorrow(id) && (
                            <button
                              className="btn btn-primary mt-3"
                              type="submit"
                            >
                              Submit
                            </button>
                          )}
                         
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </section>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default MenuUpdate;
